// The context menu engine: showMenu(x, y, items) and submenu(items), with a Back button, roles (`roles: EDIT` items
// show to editors only), radio and switch items, rows with an `icon` (a client/icons/ name, before the label),
// `disabled` items (shown greyed, their `title` saying why), `stay` items that keep it open, quick actions (`quick`: an
// icon button in a bar at the top instead of a row) and keyboard navigation. An item whose `sub` is a list of items (or
// a function answering one) and that has no action opens that list as a submenu: how an applet's menu nests. The menus themselves are built by the
// modules they belong to (menus.js for the webtop and its tiles).
import { lineIcon } from '../widgets/kit.js';
import { $colorPicker } from './color.js';
import { currentRole } from './state.js';

export const $menu = document.getElementById('menu');

// A menu is a list of items, or a function returning one (so toggles can re-render with fresh state). Submenus
// replace the menu in place; the menus above them are kept on a stack so a Back item at the top of every
// submenu returns to the previous one.
let menuPos = { x: 0, y: 0 }; // where the menu was last opened, so submenus can replace it in place
let menuStack = []; // menus above the current one, root first
let menuCurrent = null; // function returning the current menu's items
// px kept clear between the menu and the window's edges; a menu taller than the window scrolls inside that.
const MENU_MARGIN = 4;
const BACK = { label: 'Back', quick: 'back', stay: true, action: () => { menuCurrent = menuStack.pop(); renderMenu(); } };

export function showMenu(x, y, items) {
  menuPos = { x, y };
  menuStack = [];
  menuCurrent = typeof items === 'function' ? items : () => items; // a function re-renders with fresh state
  renderMenu();
}
export function submenu(items) {
  menuStack.push(menuCurrent);
  menuCurrent = typeof items === 'function' ? items : () => items;
  renderMenu();
}
// Rows minus separators left dangling (at either end, doubled, or right after a heading) once the items the
// viewer's role may not use and the quick actions are gone.
function visibleItems(kept) {
  return kept.filter((it, i) => it !== 'sep' || (i > 0 && kept[i - 1] !== 'sep' && i < kept.length - 1 && !kept[i - 1].cls?.includes('head')));
}
// Quick actions (items with `quick`) leave the list and become icon buttons in a bar at the top, after the Back
// button of a submenu; the menu's heading, when it starts with one, moves into that bar too, so a quick action never
// costs a row. Like rows, they honour `roles`, `stay`, `checked` (drawn pressed) and `cls: 'danger'`.
function splitQuick(items) {
  const quick = items.filter((it) => it !== 'sep' && it.quick);
  const rows = visibleItems(items.filter((it) => !quick.includes(it)));
  const head = quick.length && rows[0]?.cls?.includes('head') ? rows.shift() : null;
  return { quick, head, rows };
}
// The run function shared by rows and quick buttons: `stay` items (toggles, Back) leave the menu open and re-render
// it once their action is done.
const runner = (it) => async () => {
  if (!it.stay) return hideMenu(), it.action();
  await it.action();
  // Unless something closed the menu meanwhile (another webtop was opened, client/lib/nav.js): redrawing would show it again.
  if (it !== BACK && !$menu.hidden) renderMenu(true);
};
function quickBar(quick, head) {
  const li = document.createElement('li');
  li.className = 'quick';
  li.setAttribute('role', 'group');
  for (const it of quick) {
    if (it === BACK && head) li.append(quickButton(it), headNode(head)); // Back, then the heading, then the actions
    else li.append(quickButton(it));
  }
  if (head && !quick.includes(BACK)) li.prepend(headNode(head));
  return li;
}
function headNode(head) {
  const span = document.createElement('span');
  span.className = 'quick-head';
  span.textContent = head.label;
  return span;
}
function quickButton(it) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = it.cls || '';
  b.title = it.label;
  b.setAttribute('aria-label', it.label);
  b.setAttribute('role', 'menuitem');
  if (it.checked != null) { b.classList.toggle('on', !!it.checked); b.setAttribute('aria-pressed', String(!!it.checked)); }
  b.innerHTML = lineIcon(it.quick); // client/icons/<quick>.svg
  b.addEventListener('click', runner(it));
  return b;
}
const nested = (it) => (it && typeof it === 'object' && !it.action && (Array.isArray(it.sub) || typeof it.sub === 'function')
  ? { ...it, sub: true, action: () => submenu(it.sub) } : it);
// `keepScroll`: a `stay` item redrawing the same menu keeps its scroll position, so a toggle far down a long menu
// stays under the pointer; a new menu or submenu starts at the top.
function renderMenu(keepScroll = false) {
  const scroll = keepScroll ? $menu.scrollTop : 0;
  const role = currentRole();
  const all = (menuStack.length ? [BACK, ...menuCurrent()] : menuCurrent())
    .map(nested)
    .filter((it) => it === 'sep' || !it.roles || it.roles.includes(role));
  if (!all.some((it) => it !== 'sep' && (it.action || it.node))) return hideMenu(); // nothing this role can do here
  const { quick, head, rows } = splitQuick(all);
  $menu.innerHTML = '';
  if (quick.length) $menu.appendChild(quickBar(quick, head));
  for (const it of rows) {
    const li = document.createElement('li');
    if (it === 'sep') { li.className = 'sep'; $menu.appendChild(li); continue; }
    if (it.node) { li.className = 'custom'; li.append(it.node); $menu.appendChild(li); continue; } // arbitrary content
    if (it.icon) li.innerHTML = `${lineIcon(it.icon, 'row-icon')}<span></span>`; // client/icons/<icon>.svg
    (it.icon ? li.lastChild : li).textContent = it.label;
    li.tabIndex = 0;
    li.setAttribute('role', 'menuitem');
    if (it.cls) li.className = it.cls;
    if (it.title) li.title = it.title;
    if (it.disabled) { li.classList.add('disabled'); li.setAttribute('aria-disabled', 'true'); li.removeAttribute('tabindex'); }
    if (it.sub) { li.classList.add('has-sub'); li.setAttribute('aria-haspopup', 'menu'); } // opens a submenu: chevron
    if (it.radio) { // one of a set, drawn with a dot
      li.classList.add('radio', it.checked ? 'on' : 'off');
      li.setAttribute('role', 'menuitemradio');
      li.setAttribute('aria-checked', String(!!it.checked));
    } else if (it.checked != null) { // a toggle, drawn as a switch
      li.classList.add(it.checked ? 'checked' : 'unchecked');
      li.setAttribute('role', 'menuitemcheckbox');
      li.setAttribute('aria-checked', String(it.checked));
    }
    if (it.action && !it.disabled) {
      const run = runner(it);
      li.addEventListener('click', run);
      li.addEventListener('keydown', (e) => { if (e.key === 'Enter') run(); });
    }
    $menu.appendChild(li);
  }
  $menu.style.maxHeight = `${innerHeight - 2 * MENU_MARGIN}px`;
  $menu.hidden = false;
  const r = $menu.getBoundingClientRect();
  $menu.style.left = `${Math.max(MENU_MARGIN, Math.min(menuPos.x, innerWidth - r.width - MENU_MARGIN))}px`;
  $menu.style.top = `${Math.max(MENU_MARGIN, Math.min(menuPos.y, innerHeight - r.height - MENU_MARGIN))}px`;
  $menu.scrollTop = scroll;
}
export function hideMenu() { $menu.hidden = true; }

// What the arrow keys move through: the quick buttons, then the rows.
const NAV = '.quick button, li[tabindex]';

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  // Keyboard: arrows move through the items, nothing is focused until you press one.
  $menu.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...$menu.querySelectorAll(NAV)];
    const i = items.indexOf(document.activeElement);
    const n = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[n]?.focus();
  });
  addEventListener('keydown', (e) => {
    if ($menu.hidden || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || $menu.contains(document.activeElement)) return;
    e.preventDefault();
    const items = [...$menu.querySelectorAll(NAV)];
    (e.key === 'ArrowDown' ? items[0] : items.at(-1))?.focus();
  });

  addEventListener('pointerdown', (e) => { if (!$menu.hidden && !$menu.contains(e.target)) hideMenu(); });
  // Escape steps back one submenu level, and closes the menu from the top level.
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || $menu.hidden) return;
    if (menuStack.length) { menuCurrent = menuStack.pop(); renderMenu(); } else hideMenu();
  });
  addEventListener('blur', () => { if (document.activeElement !== $colorPicker) hideMenu(); }); // the native colour picker is a separate window
}
